import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

export interface TvTopListItem {
  label: string;
  value: number;
  formattedValue?: string;
}

interface TvTopListProps {
  title: string;
  items: TvTopListItem[];
  accentColor: string;
}

/** Compact ranked top-N list for the TV display — a proportional-bar substitute for DataTable,
 * since a wall-mounted screen has no mouse/keyboard for search, pagination, or export. */
export default function TvTopList({ title, items, accentColor }: TvTopListProps) {
  const maxValue = Math.max(...items.map((i) => i.value), 1);

  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: '12px',
        bgcolor: alpha('#ffffff', 0.04),
        backdropFilter: 'blur(6px)',
        boxShadow: `inset 0 0 0 1px ${alpha('#ffffff', 0.06)}`,
        p: 2.5,
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff', mb: 1.5 }}>
        {title}
      </Typography>
      <Stack spacing={1.5}>
        {items.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            —
          </Typography>
        ) : (
          items.map((item, i) => (
            <Box key={item.label}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                  {i + 1}. {item.label}
                </Typography>
                <Typography variant="body2" sx={{ color: accentColor, fontWeight: 700 }}>
                  {item.formattedValue ?? item.value.toLocaleString()}
                </Typography>
              </Stack>
              <Box sx={{ height: 6, borderRadius: '3px', bgcolor: alpha('#ffffff', 0.08), overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.max((item.value / maxValue) * 100, 3)}%`,
                    bgcolor: accentColor,
                    borderRadius: '3px',
                    boxShadow: `0 0 8px ${alpha(accentColor, 0.7)}`,
                  }}
                />
              </Box>
            </Box>
          ))
        )}
      </Stack>
    </Box>
  );
}
